/**
 * Enquiries: restaurants asking about the product, from the public site's
 * contact form, newest first.
 *
 * Kept here whether or not an email about one went out, so none is lost while
 * the platform cannot send mail. Answering happens in your own inbox; this only
 * records that somebody has, so two operators do not both reply.
 */

import {
  displayLocale,
  useMarkEnquiryAnswered,
  usePlatformEnquiries,
  type Enquiry,
} from '@restaurant/api-client';
import { ActionError, Badge, Button, Card, Empty, ErrorNotice, Loading } from '@restaurant/ui';

import { formatMoment } from '../words';

export function EnquiriesPage() {
  const enquiries = usePlatformEnquiries();
  const waiting = (enquiries.data ?? []).filter((e) => e.handled_at === null).length;

  return (
    <>
      <header className="pf__head">
        <div>
          <h1 className="pf__title">Enquiries</h1>
          <p className="pf__sub">
            From the contact form on the public site.{' '}
            {enquiries.data &&
              (waiting === 0 ? 'Every one is answered.' : `${waiting} waiting for an answer.`)}
          </p>
        </div>
      </header>

      <Card>
        {enquiries.isPending && <Loading label="Loading" />}
        {enquiries.isError && <ErrorNotice message={enquiries.error.message} />}
        {enquiries.data && enquiries.data.length === 0 && (
          <Empty
            title="No enquiries yet"
            description="When somebody fills in the contact form on the public site, it appears here."
          />
        )}
        {enquiries.data && enquiries.data.length > 0 && (
          <ul className="pf__enquiries">
            {enquiries.data.map((enquiry) => (
              <EnquiryRow key={enquiry.id} enquiry={enquiry} />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

function EnquiryRow({ enquiry }: { enquiry: Enquiry }) {
  const mark = useMarkEnquiryAnswered();
  const answered = enquiry.handled_at !== null;
  const locale = displayLocale();
  const subject = encodeURIComponent(`OnRestaurant — ${enquiry.restaurant}`);

  return (
    <li className={`pf__enquiry${answered ? ' is-answered' : ''}`}>
      <div className="pf__enquiry-head">
        <strong>{enquiry.restaurant}</strong>
        {enquiry.plan && <Badge tone="accent">{capitalise(enquiry.plan)}</Badge>}
        {answered ? <Badge tone="ok">Answered</Badge> : <Badge tone="warn">Waiting</Badge>}
        <span className="pf__enquiry-when">{formatMoment(enquiry.created_at, locale)}</span>
      </div>
      <p className="pf__enquiry-who">
        {enquiry.name} ·{' '}
        <a href={`mailto:${enquiry.email}?subject=${subject}`}>{enquiry.email}</a>
        {enquiry.phone && (
          <>
            {' '}
            · <a href={`tel:${enquiry.phone.replace(/\s+/g, '')}`}>{enquiry.phone}</a>
          </>
        )}
      </p>
      {enquiry.message && <p className="pf__enquiry-message">{enquiry.message}</p>}
      <div className="pf__enquiry-foot">
        {enquiry.page && <span className="pf__enquiry-page">From {enquiry.page}</span>}
        <Button
          size="sm"
          variant={answered ? 'ghost' : 'secondary'}
          loading={mark.isPending}
          onClick={() => mark.mutate({ id: enquiry.id, answered: !answered })}
        >
          {answered ? 'Mark not answered' : 'Mark answered'}
        </Button>
      </div>
      <ActionError error={mark.error} action="Marking the enquiry" />
    </li>
  );
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
